import { Navigate, useSearchParams } from 'react-router-dom';
import { Timer } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../lib/config';
import { Spinner } from '../../components/primitives';

const ERRORS: Record<string, string> = {
  oauth_state: 'Sign-in expired or was tampered with. Please try again.',
  oauth_failed: "We couldn't complete Google sign-in. Please try again.",
};

export function LoginScreen() {
  const { status } = useAuth();
  const [params] = useSearchParams();
  const error = params.get('error');

  if (status === 'loading')
    return (
      <div className="flex h-screen items-center justify-center bg-[#F6F7F9] dark:bg-slate-900">
        <Spinner className="h-8 w-8" />
      </div>
    );
  if (status === 'active') return <Navigate to="/" replace />;
  if (status === 'pending') return <Navigate to="/pending" replace />;

  return (
    <div className="flex h-screen items-center justify-center bg-[#F6F7F9] px-4 dark:bg-slate-900">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF5A3C]">
          <Timer className="text-white" />
        </div>
        <h1 className="text-xl font-extrabold tracking-tight">
          <span className="text-[#FF5A3C]">Food</span>
          <span className="text-ink dark:text-slate-100">Styles</span>
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Time Tracker</p>

        {error && (
          <p className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
            {ERRORS[error] ?? 'Something went wrong. Please try again.'}
          </p>
        )}

        <a
          href={`${config.apiBaseUrl}/api/auth/google`}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
        >
          <GoogleGlyph />
          Sign in with Google
        </a>
        <p className="mt-4 text-xs text-slate-400">
          Access is invite-only — ask an admin to invite your email first.
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.2 13.7 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.1-3.8 6.5-9.4 6.5-17z" />
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.3-4.5 2.1-8.8 2.1-6.4 0-11.8-4.2-13.6-9.9l-7.9 6.1C6.4 42.6 14.6 48 24 48z" />
    </svg>
  );
}
